import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { BasicTooltip, Box, Icon, InputWithIcon, SelectMenu, ExpandedTooltip } from '@livable/saas-react-components';
import { Container, Button, Chip, Grid, MenuItem, TextField, Checkbox, Tabs, Tab } from '@mui/material';
import { DataGrid, GridColumnMenu } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'src/stores';
import { exportCSVFromDataGrid } from 'src/utils/exportCSV';
import { useDelay } from 'src/utils/hooks';
import CreateAccountModal from './CreateAccountModal';
import CreateAdminModal from './CreateAdminModal';
import { AdminAccountsGrid } from './AdminAccountsGrid';
import { LivyAdminPage } from 'src/pages/LivyAdminPage';
import { SearchTab } from 'src/pages/LivyAdminPage/tabs/Search';
import { dateToFormat } from 'src/utils/date';
import useStyles from './styles';
import { TagUnits } from 'src/components/tags/TagUnits';
import { TagManagerModal } from 'src/components/tags/TagManagerModal';
import { UtilityCredentials } from './UtilityCredentials';
import { BillScraping } from './BillScraping';
export const AdminPage = observer(() => {
  const classes = useStyles();
  const store = useStore();
  const navigate = useNavigate();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const tabs = [{
    label: t('admin_page.pm_accounts'),
    name: 'pm-accounts'
  }, {
    label: t('admin_page.admin_accounts'),
    name: 'admin-accounts'
  }, {
    label: 'Livy',
    name: 'livy'
  },
  // Universal search is its own feature, not part of the assistant, so it sits beside Livy
  // rather than inside it.
  {
    label: 'Universal search',
    name: 'search'
  }];

  // Livable staff only. Both tabs are unannounced, and every endpoint behind them is
  // [Authorize(Permissions.AdminOrHigher)] and cross-company, so the tab must not appear for a
  // customer admin who would otherwise see other companies' properties listed.
  if (store.account.userProfile.isUserLivableStaff) {
    tabs.push({
      label: t('admin_page.utility_credentials'),
      name: 'utility-credentials'
    });
    tabs.push({
      label: t('admin_page.bill_scraping'),
      name: 'bill-scraping'
    });
  }
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    accountsData = _useState2[0],
    setAccountsData = _useState2[1];
  const _useState3 = useState({
      page: 0,
      pageSize: 25
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    paginationModel = _useState4[0],
    setPaginationModel = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    statusFilter = _useState6[0],
    setStatusFilter = _useState6[1];
  const _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    totalCount = _useState8[0],
    setTotalCount = _useState8[1];
  const _useState9 = useState(''),
    _useState0 = _slicedToArray(_useState9, 2),
    searchKey = _useState0[0],
    setSearchKey = _useState0[1];
  const _useState1 = useState([]),
    _useState10 = _slicedToArray(_useState1, 2),
    sortModel = _useState10[0],
    setSortModel = _useState10[1];
  const _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    searchTagsId = _useState12[0],
    setSearchTagsId = _useState12[1];
  const _useState13 = useState('pm-accounts'),
    _useState14 = _slicedToArray(_useState13, 2),
    selectedTab = _useState14[0],
    setSelectedTab = _useState14[1];
  const _useState15 = useState(false),
    _useState16 = _slicedToArray(_useState15, 2),
    triggerAdminRefresh = _useState16[0],
    setTriggerAdminRefresh = _useState16[1];
  const handleSetSortModel = async data => {
    setSortModel(data);
    if (!data.length) return;
    const searchData = {
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: searchKey,
      sortBy: data[0].field,
      sortOrder: data[0].sort
    };
    if (searchTagsId.length > 0) searchData.tagIds = searchTagsId;
    const companyListResp = await store.admin.getCompaniesListAsync.run(searchData);
    if (Array.isArray(companyListResp === null || companyListResp === void 0 ? void 0 : companyListResp.items) && (companyListResp === null || companyListResp === void 0 ? void 0 : companyListResp.totalCount) >= 0) {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const items = companyListResp.items,
        totalCount = companyListResp.totalCount;
      setAccountsData(items === null || items === void 0 ? void 0 : items.map(i => {
        return _objectSpread(_objectSpread({}, i), {}, {
          properties: {
            activeProperties: i.activeProperties,
            deactivatedProperties: i.deactivatedProperties,
            inactiveProperties: i.inactiveProperties
          }
        });
      }));
      setTotalCount(totalCount);
    }
  };
  const handleSetPaginationModel = async function (data) {
    var _sortModel$, _sortModel$2;
    let tags = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : searchTagsId;
    setPaginationModel(data);
    const searchData = {
      page: data.page + 1,
      pageSize: data.pageSize,
      search: searchKey,
      sortBy: (_sortModel$ = sortModel[0]) === null || _sortModel$ === void 0 ? void 0 : _sortModel$.field,
      sortOrder: (_sortModel$2 = sortModel[0]) === null || _sortModel$2 === void 0 ? void 0 : _sortModel$2.sort
    };
    if (tags.length > 0) searchData.tagIds = tags;
    const companyListResp = await store.admin.getCompaniesListAsync.run(searchData);
    if (Array.isArray(companyListResp === null || companyListResp === void 0 ? void 0 : companyListResp.items) && (companyListResp === null || companyListResp === void 0 ? void 0 : companyListResp.totalCount) >= 0) {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const items = companyListResp.items,
        totalCount = companyListResp.totalCount;
      setAccountsData(items === null || items === void 0 ? void 0 : items.map(i => {
        return _objectSpread(_objectSpread({}, i), {}, {
          properties: {
            activeProperties: i.activeProperties,
            deactivatedProperties: i.deactivatedProperties,
            inactiveProperties: i.inactiveProperties
          }
        });
      }));
      setTotalCount(totalCount);
    }
  };
  const loadPage = async () => {
    await store.account.getProfileAsync.run(false);
    await store.tags.getAllTagsAsync.run();
    if (!(store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin)) {
      navigate('/');
      return;
    }
    const companyListResp = await store.admin.getCompaniesListAsync.run({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: searchKey
    });
    if (Array.isArray(companyListResp === null || companyListResp === void 0 ? void 0 : companyListResp.items) && (companyListResp === null || companyListResp === void 0 ? void 0 : companyListResp.totalCount) >= 0) {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const items = companyListResp.items,
        totalCount = companyListResp.totalCount;
      setAccountsData(items === null || items === void 0 ? void 0 : items.map(i => {
        return _objectSpread(_objectSpread({}, i), {}, {
          properties: {
            activeProperties: i.activeProperties,
            deactivatedProperties: i.deactivatedProperties,
            inactiveProperties: i.inactiveProperties
          }
        });
      }));
      setTotalCount(totalCount);
    }
  };
  useDelay(() => loadPage(), [searchKey], 700);
  const handleFilterOption = event => {
    let currentTags = '';
    setStatusFilter(event.target.value);
    if (!event.target.value.length) {
      setSearchTagsId('');
    } else {
      currentTags = event.target.value.join('&');
      setSearchTagsId(event.target.value.join('&'));
    }
    handleSetPaginationModel(paginationModel, currentTags);
  };
  const handleResetTags = () => {
    setSearchTagsId('');
    setStatusFilter([]);
    handleSetPaginationModel(paginationModel, '');
  };
  const handleLogin = async (error, data) => {
    try {
      store.admin.setIsAdminSwitchToNewAccount(true);
      await store.admin.getAdminAccountLoginImpersonateAsync.run(_objectSpread({}, data.row));
      await store.account.refreshAccessTokenAsync.run();
      await store.portfolios.getExistingPortfoliosAsync.run();
      await store.company.getCompaniesAsync.run();
      store.admin.saveSelectedCompanyName(data.row.companyName);
      navigate('/active_dashboard');
      window.location.reload();
      store.admin.setIsAdminSwitchToNewAccount(false);
    } catch (currentError) {
      console.log('Error', currentError);
      store.admin.setIsAdminSwitchToNewAccount(false);
    }
  };
  useEffect(() => {
    try {
      loadPage();
    } catch (e) {
      console.log('useEffect Error', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleCellClick = () => {
    // TODO: TBD when defined functionality
  };
  const refreshTrigger = () => {
    loadPage();
  };
  const indicators = /*#__PURE__*/React.createElement("span", {
    className: "indicators"
  }, /*#__PURE__*/React.createElement("span", {
    className: "inline-arrow"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "arrow-down"
  })), /*#__PURE__*/React.createElement("span", {
    className: "inline-filter"
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "equalizer"
  })));
  const renderHeader = (text, rightAlign) => /*#__PURE__*/React.createElement("span", {
    className: rightAlign ? classes.rightAlign : ''
  }, text, indicators);
  const columns = [{
    field: 'code',
    headerName: t('admin_page.company_id'),
    isExportable: true,
    width: 120,
    align: 'right',
    renderHeader: () => renderHeader(t('admin_page.company_id')),
    valueGetter: params => +(params.row.code || params.row.companyId).replace(/^\D+/g, ''),
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.leftAlign)
    }, params.row.code || params.row.companyId)
  }, {
    field: 'companyName',
    headerName: t('admin_page.company_name'),
    isExportable: true,
    width: 195,
    align: 'right',
    renderHeader: () => renderHeader(t('admin_page.company_name')),
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.leftAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: params.row.companyName,
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", null, params.row.companyName))),
    valueGetter: params => params.row.companyName
  }, {
    field: 'email',
    headerName: t('admin_page.email'),
    isExportable: true,
    width: 245,
    align: 'right',
    renderHeader: () => renderHeader(t('admin_page.email')),
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.leftAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: params.row.email,
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", null, params.row.email || 'N/A'))),
    valueGetter: params => params.row.email
  }, {
    field: 'phone',
    headerName: t('admin_page.phone'),
    isExportable: true,
    width: 130,
    align: 'right',
    renderHeader: () => renderHeader(t('admin_page.phone')),
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.leftAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: params.row.phone,
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", null, params.row.phone || 'N/A'))),
    valueGetter: params => {
      var _params$row$phone;
      return (_params$row$phone = params.row.phone) === null || _params$row$phone === void 0 ? void 0 : _params$row$phone.replace('+', '');
    }
  }, {
    field: 'dateCreated',
    headerName: t('admin_page.date_created'),
    isExportable: true,
    width: 110,
    align: 'right',
    renderHeader: () => renderHeader(t('admin_page.date_created')),
    valueGetter: params => dateToFormat(params.row.createdOn),
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.leftAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: dateToFormat(params.row.createdOn),
      placement: "top"
    }, /*#__PURE__*/React.createElement("span", null, params.row.createdOn ? dateToFormat(params.row.createdOn) : 'N/A')))
  }, {
    field: 'activePropertiesCount',
    width: 260,
    align: 'right',
    renderHeader: () => renderHeader(t('admin_page.properties')),
    valueGetter: params => params.row.properties.activeProperties,
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.leftAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t('admin_page.active_properties'),
      placement: "top"
    }, /*#__PURE__*/React.createElement(Chip, {
      className: classes.chip,
      size: "large",
      icon: /*#__PURE__*/React.createElement(Icon, {
        type: "building"
      }),
      label: String(params.row.properties.activeProperties).padStart(3, ' '),
      variant: "success"
    })), /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t('admin_page.inactive_properties'),
      placement: "top"
    }, /*#__PURE__*/React.createElement(Chip, {
      className: classes.chip,
      size: "large",
      icon: /*#__PURE__*/React.createElement(Icon, {
        type: "edit"
      }),
      label: String(params.row.properties.inactiveProperties).padStart(3, ' '),
      variant: "info"
    })), /*#__PURE__*/React.createElement(BasicTooltip, {
      title: t('admin_page.deactivated_properties'),
      placement: "top"
    }, /*#__PURE__*/React.createElement(Chip, {
      className: classes.chip,
      size: "large",
      icon: /*#__PURE__*/React.createElement(Icon, {
        type: "building-minus"
      }),
      label: String(params.row.properties.deactivatedProperties).padStart(3, ' '),
      variant: "inactive"
    })))
  }, {
    field: 'tags',
    width: 130,
    headerAlign: 'left',
    align: 'left',
    headerName: t('admin_page.tags'),
    filterable: false,
    sortable: false,
    hideable: false,
    renderCell: params => /*#__PURE__*/React.createElement(TagUnits, {
      tags: params.row.tagIds,
      companyId: params.row.companyId,
      trigger: refreshTrigger
    })
  }, {
    field: 'toolbox',
    width: 90,
    headerAlign: 'right',
    align: 'left',
    headerName: t('admin_page.toolbox'),
    filterable: false,
    sortable: false,
    hideable: false,
    renderCell: params => /*#__PURE__*/React.createElement("div", {
      className: "".concat(classes.cellContent, " ").concat(classes.rightAlign)
    }, /*#__PURE__*/React.createElement(BasicTooltip, {
      title: "".concat(t('admin_page.login_as'), " ").concat(params.row.companyName),
      placement: "top"
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      disabled: store.admin.isAdminSwitchToNewAccount,
      onClick: e => handleLogin(e, params),
      'custom-attribute': '19-admin-page'
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "access-key"
    }))))
  }];
  const handleDownloadCsv = () => {
    exportCSVFromDataGrid({
      columns,
      name: 'pm_accounts.csv',
      rows: accountsData
    });
  };
  const CustomColumnMenu = props => {
    return /*#__PURE__*/React.createElement(GridColumnMenu, _extends({}, props, {
      slots: {
        columnMenuUserItem: () => /*#__PURE__*/React.createElement("div", {
          className: classes.exportCSV
        }, /*#__PURE__*/React.createElement(Button, {
          onClick: handleDownloadCsv,
          size: "small",
          variant: "contained"
        }, "Export CSV"))
      },
      slotProps: {
        columnMenuUserItem: {
          displayOrder: 15
        }
      }
    }));
  };
  const handleTabChange = (e, newValue) => {
    if (newValue) setSelectedTab(newValue);
  };
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement("div", {
    className: classes.pageWrapper
  }, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    rowSpacing: 10,
    columnSpacing: 10,
    className: classes.createButtons
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, /*#__PURE__*/React.createElement(CreateAccountModal, {
    trigger: refreshTrigger
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true
  }, (store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin) && /*#__PURE__*/React.createElement(CreateAdminModal, {
    trigger: refreshTrigger,
    triggerAdminRefresh: () => setTriggerAdminRefresh(!triggerAdminRefresh)
  }))), store.account.userProfile.isUserSuperAdmin ? /*#__PURE__*/React.createElement(Tabs, {
    "aria-label": "tabs",
    onChange: handleTabChange,
    type: "navigation",
    value: selectedTab
  }, tabs.map(tab => /*#__PURE__*/React.createElement(Tab, {
    key: tab.name,
    label: tab.label,
    value: tab.name
  }))) : null, selectedTab === 'search' ? /*#__PURE__*/React.createElement(SearchTab, null) : selectedTab === 'livy' ? /*#__PURE__*/React.createElement(LivyAdminPage, null) : selectedTab === 'pm-accounts' || store.account.userProfile.isUserAdmin ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classes.filtersWrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.searchWrapper
  }, /*#__PURE__*/React.createElement(InputWithIcon, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "search"
    })
  }, /*#__PURE__*/React.createElement(TextField, {
    id: "company-search",
    label: t('general.search'),
    variant: "outlined",
    value: searchKey,
    onChange: event => {
      var _event$target;
      return setSearchKey((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.value);
    },
    fullWidth: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: classes.sortByStatusWrapper
  }, /*#__PURE__*/React.createElement(SelectMenu, {
    multiple: true,
    id: "sort-by-tags",
    label: t('admin_page.filter_by_tags'),
    value: statusFilter,
    className: classes.filterSelector,
    onChange: e => handleFilterOption(e),
    disabled: store.tags.tagsList.length === 0,
    renderValue: selected => {
      return selected.map(i => store.tags.getTagById(i).name).join(', ');
    }
  }, store.tags.tagsList.map(tag => {
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: tag.id,
      value: tag.id,
      variant: "multiple"
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: statusFilter.indexOf(tag.id) > -1
    }), tag.name);
  })), statusFilter && statusFilter.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: classes.clearSortByStatusWrapper
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    type: "grey",
    title: t('admin_page.clear_filter'),
    placement: "top"
  }, /*#__PURE__*/React.createElement("span", {
    onClick: handleResetTags
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "delete"
  }))))), /*#__PURE__*/React.createElement("div", {
    className: classes.tagManager
  }, /*#__PURE__*/React.createElement(TagManagerModal, {
    disabled: store.tags.tagsList.length === 0,
    trigger: refreshTrigger,
    handleResetTags: handleResetTags
  }))), /*#__PURE__*/React.createElement(DataGrid, {
    autoHeight: true,
    columns: columns,
    getRowHeight: () => 'auto',
    columnHeaderHeight: 77,
    disableRowSelectionOnClick: true,
    getRowClassName: () => classes.row,
    getRowId: row => row.companyId + row.portfolioId,
    loading: store.admin.getCompaniesListAsync.inProgress,
    initialState: {
      pagination: {
        paginationModel
      }
    },
    onCellClick: handleCellClick,
    paginationMode: 'server',
    rowCount: totalCount,
    rowLength: totalCount,
    rows: accountsData,
    paginationModel: paginationModel,
    sortModel: sortModel,
    onSortModelChange: model => handleSetSortModel(model),
    onPaginationModelChange: model => handleSetPaginationModel(model),
    slotProps: {
      filterPanel: {
        filterFormProps: {
          columnInputProps: {
            variant: 'outlined'
          },
          operatorInputProps: {
            variant: 'outlined'
          }
        }
      }
    },
    slots: {
      BaseButton: props => /*#__PURE__*/React.createElement(Button, _extends({}, props, {
        variant: "actionButton",
        'custom-attribute': '20-admin-page'
      })),
      BaseTextField: props => /*#__PURE__*/React.createElement(TextField, _extends({}, props, {
        variant: "outlined"
      })),
      columnMenu: CustomColumnMenu
    }
  })) : selectedTab === 'utility-credentials' ?
  /*#__PURE__*/
  // Matched explicitly, not as the fallback: the tab only exists for super admins, so a
  // catch-all would render it to a plain admin whenever selectedTab is anything else.
  React.createElement(UtilityCredentials, null) : selectedTab === 'bill-scraping' ?
  /*#__PURE__*/
  // Explicit, not a fallback: staff-only surface.
  React.createElement(BillScraping, null) : /*#__PURE__*/React.createElement(AdminAccountsGrid, {
    externalTriggerRefresh: triggerAdminRefresh
  }))));
});